import { ChartColorsMap, KeysCsv, SENSOR_PREFIXES, SOIL_DEPTH_REGEX } from '../constants/index.jsx';

export const GetChartsPeriodsMap = (t) => ({
  week: {
    value: 7,
    label: t('periods.week'),
  },
  month: {
    value: 31,
    label: t('periods.month'),
  },
  quarter: {
    value: 92,
    label: t('periods.quarter'),
  },
  year: {
    value: 365,
    label: t('periods.year'),
  },
});

export const GetChartsTitlesMap = (t) => ({
  temperature: `${t('metrics.airTemperature')}, °C`,
  temperatureHourly: `${t('metrics.airTemperature')}, °C (${t('units.hourlyData')})`,
  gdd: t('metrics.gddDaily'),
  ndvi: 'NDVI',
  humidity: t('metrics.humidity'),
  dew: t('metrics.dewfallDuration'),
  bbch: 'BBCH',
  soilMoisture: `${t('metrics.soilMoisture')}, %`,
  soilTemperature: `${t('metrics.soilTemperature')}, °C`,
  soilSalinity: `${t('metrics.salinity')}, dS/m`,
  soilBbch: `BBCH, ${t('units.percentPerHour')}`,
  rain: `${t('metrics.rain')}, %/h`,
});

export const GetChartsMap = (t) => ({
  temperature: {
    id: 'temperature',
    title: t('metrics.airTemperature'),
    units: '°C',
    color: ChartColorsMap.temperature,
    colorCompared: ChartColorsMap.temperatureCompared,
    csvKeys: KeysCsv.temperature,
    csvFileName: 'temperature',
  },
  humidity: {
    id: 'humidity',
    title: t('metrics.humidity'),
    units: '%',
    color: ChartColorsMap.humidity,
    colorCompared: ChartColorsMap.humidityCompared,
    csvKeys: KeysCsv.humidity,
    csvFileName: 'humidity',
  },
  dew: {
    id: 'dew',
    title: t('metrics.dewfallDuration'),
    units: 'min',
    color: ChartColorsMap.dew,
    colorCompared: ChartColorsMap.dewCompared,
    csvKeys: KeysCsv.dew,
    csvFileName: 'dew_point',
  },
  gdd: {
    id: 'gdd',
    title: t('metrics.gddDaily'),
    units: t('units.degreesDays'),
    color: ChartColorsMap.gdd,
    colorCompared: ChartColorsMap.gddCompared,
    csvKeys: KeysCsv.gdd,
    csvFileName: 'gdd',
  },
  ndvi: {
    id: 'ndvi',
    title: 'NDVI',
    units: '',
    color: ChartColorsMap.ndvi,
    colorCompared: ChartColorsMap.ndviCompared,
    csvKeys: KeysCsv.ndvi,
    csvFileName: 'ndvi',
  },
  bbch: {
    id: 'bbch',
    title: `BBCH`,
    units: t('units.percentPerHour'),
    color: ChartColorsMap.bbch,
    colorCompared: ChartColorsMap.bbchCompared,
    csvKeys: KeysCsv.bbch,
    csvFileName: 'bbch',
  },
  soilHumidity: {
    id: 'soilHumidity',
    title: t('metrics.soilMoisture'),
    units: '%',
    color: ChartColorsMap.soilHumidity,
    colorCompared: ChartColorsMap.soilHumidityCompared,
    csvKeys: '',
    csvFileName: 'soil_humidity',
  },
  soilTemperature: {
    id: 'soilTemperature',
    title: t('metrics.soilTemperature'),
    units: '°C',
    color: ChartColorsMap.soilTemperature,
    colorCompared: ChartColorsMap.soilTemperatureCompared,
    csvKeys: KeysCsv.bbch,
    csvFileName: 'soil_temperature',
  },
  salinity: {
    id: 'salinity',
    title: t('metrics.salinity'),
    units: 'dS/m',
    color: ChartColorsMap.salinity,
    colorCompared: ChartColorsMap.salinityCompared,
    csvKeys: KeysCsv.salinity,
    csvFileName: 'salinity',
  },
  rain: {
    id: 'rain',
    title: t('metrics.rain'),
    units: '%/h',
    color: ChartColorsMap.rain,
    colorCompared: ChartColorsMap.rain,
    csvKeys: KeysCsv.rain,
    csvFileName: 'rain',
  },
  leafArea: {
    id: 'leafArea',
    title: t('metrics.leafArea'),
    units: '%',
    color: ChartColorsMap.leafArea,
    colorCompared: ChartColorsMap.leafAreaCompared,
    csvKeys: KeysCsv.leafArea,
    csvFileName: 'leaf_area',
  },
  leafNdvi: {
    id: 'leafNdvi',
    title: t('metrics.leafNdvi'),
    units: '',
    color: ChartColorsMap.leafNDVI,
    colorCompared: ChartColorsMap.leafNDVICompared,
    csvKeys: KeysCsv.leafNdvi,
    csvFileName: 'leaf_ndvi',
  },
  soilNdvi: {
    id: 'soilNdvi',
    title: t('metrics.soilNdvi'),
    units: '',
    color: ChartColorsMap.soilNDVI,
    colorCompared: ChartColorsMap.soilNDVICompared,
    csvKeys: KeysCsv.soilNdvi,
    csvFileName: 'soil_ndvi',
  },
});

const GetRawValues = ({ min, max, steps }) => {
  const interval = (max - min) / (steps - 1);
  return Array.from({ length: steps }, (_, i) => min + i * interval);
};

// GDD YAsis ticks array
export const GetGddTicks = ({ values, steps }) => {
  let minY = Math.min(...values);
  let maxY = Math.max(...values);

  minY = Math.floor(minY / 10) * 10;
  maxY = Math.ceil(maxY / 10) * 10;

  const rawValues = GetRawValues({ max: maxY, min: minY, steps });
  const roundedValues = rawValues.map((num) => Math.ceil(num / 10) * 10);

  const uniqueValues = [...new Set(roundedValues)];

  return { ticks: uniqueValues, min: minY, max: maxY };
};

// Soil Salinity YAsis ticks array
export const GetSoilSalinityTicks = ({ values, steps }) => {
  let minY = Math.min(...values);
  let maxY = Math.max(...values);

  minY = Math.ceil(minY);
  maxY = Math.ceil(maxY);

  const rawValues = GetRawValues({ max: maxY, min: minY, steps });
  const roundedValues = rawValues.map((num) => Math.ceil(num));

  const uniqueValues = [...new Set(roundedValues)];

  return { ticks: uniqueValues, min: minY, max: maxY };
};

// Dew YAsis ticks array
export const GetDewTicks = ({ values, steps }) => {
  const minY = Math.min(...values);
  const maxY = Math.max(...values);

  const rawValues = GetRawValues({ min: minY, max: maxY, steps });

  return { ticks: [...new Set(rawValues)], min: minY, max: maxY };
};

export const GetRainTicks = ({ values, steps }) => {
  const minY = Math.min(...values);
  const maxY = Math.max(...values);
  const d = maxY < 1 ? 100 : 1;

  let rawValues = GetRawValues({
    min: Math.floor(minY * d) / d,
    max: Math.ceil(maxY * d) / d,
    steps,
  });

  rawValues = rawValues.map((num, i) => (i === 0 ? Math.floor(num * d) / d : Math.ceil(num * d) / d));

  const uniqueValues = [...new Set(rawValues)];

  return { ticks: uniqueValues, min: minY, max: maxY };
};

export const GetTemperatureTicks = ({ min, max, steps }) => {
  if (!steps || steps < 3) {
    steps = 3;
  }

  const data = GetRawValues({ min: min, max, steps });
  const result = [];
  data.map((item) => {
    const num = Number(item);
    result.push(num < 0 ? Math.floor(num) : Math.ceil(num));
  });
  return [...new Set(result)];
};

export const GetBrushPosition = ({ data, period }) => {
  if (!data) return [0, 0];
  const end = data.length > 0 ? data.length - 1 : 0;
  const start = end > period ? end - period : 0;
  return [start, end];
};

export const MakeTicks = ({ value, count = 10, stepBase = 50 }) => {
  const max = Math.ceil(value / stepBase) * stepBase;
  const step = Math.ceil(max / (count - 1) / stepBase) * stepBase;
  return Array.from({ length: count }, (_, i) => i * step);
};

export const GetChartTitle = (key, chartsMap) => {
  const match = SENSOR_PREFIXES.find((prefix) => key.includes(prefix));
  if (match) {
    return `${chartsMap[match].title} (${key.slice(-2)} cm)`;
  }
  return chartsMap[key]?.title || key;
};

export const GetSoilDepthMatch = (id) => id.match(SOIL_DEPTH_REGEX);

export const ResolvePairMetricData = (id, { metricsAir, metricsNdvi, metricsVegetation, metricsSoil }) => {
  const match = GetSoilDepthMatch(id);
  if (match) {
    const [, type] = match;
    return metricsSoil?.[type]?.dataByDays?.[id];
  }
  return metricsAir?.[id] || metricsNdvi?.[id] || metricsVegetation?.[id];
};

export const FormatPair = (pair) => {
  let depth = [];
  const formatted = pair.map((p) => {
    const match = SENSOR_PREFIXES.find((prefix) => p.includes(prefix));
    if (match) {
      depth.push(Number(p.slice(-2)));
      return match;
    }
    depth.push(0);
    return p;
  });
  return { formatted, depth };
};

// CSV keys
export const GetSoilCsvKeys = (item) => {
  let keys = 'Date';
  for (let index = 1; index <= 9; index++) {
    const key = `value${index}0`;
    if (item[key] !== undefined && item[key] !== null) {
      keys = keys + ', ' + `${index}0 cm`;
    }
  }
  return keys;
}